import {
  Box,
  SwitchIndicator,
  TextField,
  FieldData,
  useForm,
  SubAccountPermissionSection,
  SubAccountPermissionItem,
  withStyles,
  MaterialSymbol,
  sanitizeToNumeric,
} from 'vopay';

import { ChangeEvent, useImperativeHandle, useMemo, useEffect, Ref, FC } from 'react';
import { useTranslation } from 'react-i18next';

import { BodyText } from 'vopay';
import { Title, UtilButton } from 'UI';
import CollapsibleList from './CollapsibleList';
import SettingsGroup from './SettingsGroup';
import styles from './AccountPermissionForm.module.scss';

const classes = withStyles(styles);
type PermissionFormValues = {
  [key: string]: boolean | string | number;
};

type AccountPermissionFormProps = {
  onAccountPermissionSave: (values: PermissionFormValues) => void;
  permissions: SubAccountPermissionSection[];
  ref: Ref<AccountPermissionFormRef>;
  onFormStateChange: (hasChanges: boolean, isValid: boolean) => void;
  onSearchChange: (isSearching: boolean) => void;
  searchTerm: string;
};

export type AccountPermissionFormRef = {
  enableAll: () => void;
};

type ToggleProps = {
  name: string;
  checked: boolean;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
};

const Toggle: FC<ToggleProps> = ({ name, checked, onChange }) => {
  return (
    <>
      <SwitchIndicator checked={checked} />
      <input
        className={classes('hiddenCheckbox')}
        id={`checkbox-${name}`}
        type="checkbox"
        name={name}
        checked={checked}
        onChange={onChange}
        data-checked={checked}
      />
    </>
  );
};

// Helper to check if SubItems is an array of permissions or sections
function isPermissionArray(
  subItems: SubAccountPermissionItem[] | SubAccountPermissionSection[],
): subItems is SubAccountPermissionItem[] {
  if (!subItems || subItems.length === 0) return true;
  return 'Permission' in subItems[0];
}

// Flatten all permissions from nested structure
function flattenPermissions(sections: SubAccountPermissionSection[]): SubAccountPermissionItem[] {
  const result: SubAccountPermissionItem[] = [];

  function traverse(items: SubAccountPermissionItem[] | SubAccountPermissionSection[]) {
    if (isPermissionArray(items)) {
      result.push(...items);
    } else {
      items.forEach((section) => traverse(section.SubItems));
    }
  }

  sections.forEach((section) => traverse(section.SubItems));
  return result;
}

// Build initial form values from flattened permissions
function buildInitialValues(sections: SubAccountPermissionSection[]): PermissionFormValues {
  const allPermissions = flattenPermissions(sections);
  return allPermissions.reduce((acc, permission) => {
    return {
      ...acc,
      [permission.Permission]: permission.Type === 'number' ? String(permission.Value) : permission.Value,
    };
  }, {});
}

// Build field config from permissions
function buildPermissionsConfig(permissions: SubAccountPermissionItem[]): AccountPermissionFieldData[] {
  return permissions.map((permission) => ({
    fieldName: permission.Permission,
    label: () => permission.Label,
    getInputState: () => 'optional',
    showField: () => true,
    serializeField: (values) =>
      permission.Type === 'number'
        ? Number(sanitizeToNumeric(String(values[permission.Permission])))
        : values[permission.Permission],
    renderField: ({ getFormikData }) => {
      if (permission.Type === 'boolean') {
        const fieldData = getFormikData<boolean>({});
        return <Toggle name={fieldData.name} checked={!!fieldData.value} onChange={fieldData.onChange} />;
      } else if (permission.Type === 'number') {
        const fieldData = getFormikData<number>({});
        return (
          <TextField
            variant="outlineShadow"
            preset="currency"
            name={fieldData.name}
            value={String(fieldData.value)}
            onBlur={fieldData.onBlur}
            onChange={fieldData.onChange}
          />
        );
      } else {
        // string type
        const fieldData = getFormikData<string>({});
        return (
          <TextField
            variant="outlineShadow"
            name={fieldData.name}
            value={fieldData.value}
            onBlur={fieldData.onBlur}
            onChange={fieldData.onChange}
          />
        );
      }
    },
  }));
}

type AccountPermissionFieldData = Exclude<FieldData<PermissionFormValues>, 'label'> & {
  label: () => string;
};

const AccountPermissionForm: FC<AccountPermissionFormProps> = ({
  onAccountPermissionSave,
  permissions,
  onFormStateChange,
  onSearchChange,
  searchTerm,

  ref,
}) => {
  const { t } = useTranslation('translations', { keyPrefix: 'SubAccounts.Permissions.form' });

  const allPermissions = useMemo(() => flattenPermissions(permissions), [permissions]);
  const initialValues = useMemo(() => buildInitialValues(permissions), [permissions]);
  const permissionsConfig = useMemo(() => buildPermissionsConfig(allPermissions), [allPermissions]);

  const { ctx, renderFormElements } = useForm<PermissionFormValues>({
    config: permissionsConfig,
    initialValues,
    onSubmit: (values) => {
      // Filter out hidden fields (fields whose dependencies aren't satisfied)
      const visiblePermissions = allPermissions.filter((perm) => isDependencySatisfied(perm));
      const visibleValues: PermissionFormValues = {};

      visiblePermissions.forEach((perm) => {
        if (values[perm.Permission] !== undefined) {
          visibleValues[perm.Permission] = values[perm.Permission];
        }
      });

      onAccountPermissionSave(visibleValues);
    },
  });

  // Check if form has changes and is valid
  const hasChanges = useMemo(() => {
    return JSON.stringify(ctx.values) !== JSON.stringify(initialValues);
  }, [ctx.values, initialValues]);

  // Notify parent of form state changes
  useEffect(() => {
    if (onFormStateChange) {
      onFormStateChange(hasChanges, ctx.isValid);
    }
  }, [hasChanges, ctx.isValid, onFormStateChange]);

  // Notify parent when search state changes
  useEffect(() => {
    if (onSearchChange) {
      onSearchChange(searchTerm.trim().length > 0);
    }
  }, [searchTerm, onSearchChange]);

  // Function to enable all boolean permissions
  const enableAll = () => {
    const updates: Record<string, boolean> = {};
    allPermissions.forEach((permission) => {
      if (permission.Type === 'boolean') {
        updates[permission.Permission] = true;
      }
    });
    ctx.setValues({ ...ctx.values, ...updates });
  };

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    enableAll,
  }));

  // Check if a permission's dependencies are satisfied
  const isDependencySatisfied = (permission: SubAccountPermissionItem): boolean => {
    if (!permission.Dependencies || permission.Dependencies.length === 0) {
      return true;
    }
    return permission.Dependencies.some((dep) => ctx.values[dep] === true);
  };

  // Filter permissions based on dependencies
  const filterByDependencies = (permissions: SubAccountPermissionItem[]): SubAccountPermissionItem[] => {
    return permissions.filter((perm) => isDependencySatisfied(perm));
  };

  // Recursively count all boolean permissions in a section tree
  const countAllPermissionsInSection = (section: SubAccountPermissionSection): { enabled: number; total: number } => {
    if (isPermissionArray(section.SubItems)) {
      const visiblePerms = filterByDependencies(section.SubItems as SubAccountPermissionItem[]);
      const booleanPerms = visiblePerms.filter((p) => p.Type === 'boolean');
      const total = booleanPerms.length;
      const enabled = booleanPerms.filter((p) => ctx.values[p.Permission] === true).length;
      return { enabled, total };
    } else {
      // Branch node - sum up all nested sections
      const nestedSections = section.SubItems as SubAccountPermissionSection[];
      return nestedSections.reduce(
        (acc, subSection) => {
          const stats = countAllPermissionsInSection(subSection);
          return {
            enabled: acc.enabled + stats.enabled,
            total: acc.total + stats.total,
          };
        },
        { enabled: 0, total: 0 },
      );
    }
  };

  // Get all permissions from a section recursively
  const getAllPermissionsFromSection = (section: SubAccountPermissionSection): SubAccountPermissionItem[] => {
    if (isPermissionArray(section.SubItems)) {
      return section.SubItems as SubAccountPermissionItem[];
    } else {
      const nestedSections = section.SubItems as SubAccountPermissionSection[];
      return nestedSections.flatMap((subSection) => getAllPermissionsFromSection(subSection));
    }
  };

  // Count enabled permissions in a group
  const countEnabledInGroup = (permissions: SubAccountPermissionItem[]) => {
    const visiblePerms = filterByDependencies(permissions);
    const booleanPerms = visiblePerms.filter((p) => p.Type === 'boolean');
    const total = booleanPerms.length;
    const enabled = booleanPerms.filter((p) => ctx.values[p.Permission] === true).length;
    return { enabled, total };
  };

  // Enable all boolean permissions in a group
  const enableAllInGroup = (permissions: SubAccountPermissionItem[]) => {
    const visiblePerms = filterByDependencies(permissions);
    const updates: Record<string, boolean> = {};
    visiblePerms.forEach((permission) => {
      if (permission.Type === 'boolean') {
        updates[permission.Permission] = true;
      }
    });
    ctx.setValues({ ...ctx.values, ...updates });
  };

  // Disable all boolean permissions in a group
  const disableAllInGroup = (permissions: SubAccountPermissionItem[]) => {
    const visiblePerms = filterByDependencies(permissions);
    const updates: Record<string, boolean> = {};
    visiblePerms.forEach((permission) => {
      if (permission.Type === 'boolean') {
        updates[permission.Permission] = false;
      }
    });
    ctx.setValues({ ...ctx.values, ...updates });
  };

  // Filter sections based on search term and dependencies
  const filteredSections = useMemo(() => {
    function filterSection(section: SubAccountPermissionSection): SubAccountPermissionSection | null {
      const lowerSearchTerm = searchTerm.toLowerCase();
      const sectionNameMatches = !searchTerm.trim() || section.SectionName.toLowerCase().includes(lowerSearchTerm);

      if (isPermissionArray(section.SubItems)) {
        // Leaf node - filter permissions by dependencies and search
        let perms = section.SubItems as SubAccountPermissionItem[];

        // Filter by dependencies first
        perms = filterByDependencies(perms);

        // Then filter by search term if searching
        if (searchTerm.trim()) {
          perms = perms.filter((perm) => perm.Label.toLowerCase().includes(lowerSearchTerm));
        }

        // Hide section if no visible permissions
        if (perms.length === 0) {
          return null;
        }

        if (sectionNameMatches || perms.length > 0) {
          return {
            ...section,
            SubItems: perms,
          };
        }
        return null;
      } else {
        // Branch node - filter nested sections recursively
        const filteredSubSections = section.SubItems.map((subSection) => filterSection(subSection)).filter(
          (s) => s !== null,
        ) as SubAccountPermissionSection[];

        // Hide section if no visible subsections
        if (filteredSubSections.length === 0) {
          return null;
        }

        if (sectionNameMatches || filteredSubSections.length > 0) {
          return {
            ...section,
            SubItems: filteredSubSections,
          };
        }
        return null;
      }
    }

    return permissions
      .map((section) => filterSection(section))
      .filter((s) => s !== null) as SubAccountPermissionSection[];
  }, [permissions, searchTerm, ctx.values]); // Add ctx.values to recalculate when permissions change

  const hasFilteredResults = filteredSections.length > 0;
  const isFiltering = searchTerm.trim().length > 0;

  // Recursive function to render sections
  const renderSection = (section: SubAccountPermissionSection, depth: number = 0) => {
    const isLeaf = isPermissionArray(section.SubItems);

    if (isLeaf) {
      // Leaf node - render permissions
      const perms = section.SubItems as SubAccountPermissionItem[];
      const visiblePerms = filterByDependencies(perms);
      const stats = countEnabledInGroup(perms);
      const hasBooleanFields = stats.total > 0;
      const allEnabled = hasBooleanFields && stats.enabled === stats.total;

      // Check if this section should be rendered as non-collapsible SettingsGroup
      const isGlobalVelocities = section.SectionName === 'Global Velocities';

      if (isGlobalVelocities) {
        return (
          <div key={section.SectionId} style={{ '--trigger-depth': depth }} className={classes('sectionWrapper')}>
            <SettingsGroup>
              <SettingsGroup.Header>{section.SectionName}</SettingsGroup.Header>
              {visiblePerms.map((permission) => {
                const fieldConfig = permissionsConfig.find((f) => f.fieldName === permission.Permission);
                if (!fieldConfig) return null;
                const isBoolean = permission.Type === 'boolean';

                return renderFormElements({
                  config: [fieldConfig],
                  override: (fieldData) => {
                    // Wrap in label for boolean fields to make the whole row clickable
                    if (isBoolean) {
                      return (
                        <SettingsGroup.Row key={permission.Permission}>
                          <BodyText className={classes('permissionLabelText')} weight="md">
                            {permission.Label}
                          </BodyText>
                          <div className={classes('permissionFieldWrapper')}>
                            {' '}
                            <label
                              htmlFor={`checkbox-${fieldData.fieldName}`}
                              style={{ cursor: 'pointer', display: 'block' }}
                            >
                              {fieldData.template}
                            </label>
                          </div>
                        </SettingsGroup.Row>
                      );
                    }

                    return (
                      <SettingsGroup.Row key={permission.Permission}>
                        <BodyText className={classes('permissionLabelText')} weight="md">
                          {permission.Label}
                        </BodyText>
                        <div className={classes('permissionFieldWrapper')}>{fieldData.template}</div>
                      </SettingsGroup.Row>
                    );
                  },
                });
              })}
            </SettingsGroup>
          </div>
        );
      }

      // Group permissions: each boolean gets its own group with fields that have matching Section
      const permissionGroups: {
        mainPermission: SubAccountPermissionItem | null;
        dependents: SubAccountPermissionItem[];
      }[] = [];
      const processedPermissions = new Set<string>();

      // First, find all boolean permissions and group with their section-matched fields
      const booleanPerms = visiblePerms.filter((p) => p.Type === 'boolean');

      booleanPerms.forEach((boolPerm) => {
        // Find non-boolean fields whose Section property matches this boolean's Label
        const dependents = visiblePerms.filter((p) => p.Type !== 'boolean' && p.Section === boolPerm.Label);

        permissionGroups.push({ mainPermission: boolPerm, dependents });
        processedPermissions.add(boolPerm.Permission);
        dependents.forEach((d) => processedPermissions.add(d.Permission));
      });

      // Handle non-boolean permissions whose Section matches the parent section ID (create ONE group for all)
      const standaloneNonBooleans = visiblePerms.filter(
        (p) => p.Type !== 'boolean' && !processedPermissions.has(p.Permission) && p.Section === section.SectionId,
      );

      if (standaloneNonBooleans.length > 0) {
        permissionGroups.push({ mainPermission: null, dependents: standaloneNonBooleans });
        standaloneNonBooleans.forEach((perm) => processedPermissions.add(perm.Permission));
      }

      // Default: render as collapsible
      return (
        <CollapsibleList.Item key={section.SectionId} open={isFiltering} style={{ '--trigger-depth': depth }}>
          <CollapsibleList.Trigger className={depth === 0 ? styles.baseTrigger : styles.nestedTrigger}>
            <div className={classes('groupHeader')}>
              <div className={classes('groupInfo')}>
                {depth === 0 ? (
                  <Title level="5">{section.SectionName}</Title>
                ) : (
                  <BodyText className={classes('subCollapsibleTitle')}>{section.SectionName}</BodyText>
                )}
                {hasBooleanFields && (
                  <BodyText weight="rg" className={classes('groupCount')}>
                    ({stats.enabled}/{stats.total})
                  </BodyText>
                )}
              </div>
              {hasBooleanFields && !isFiltering && (
                <div onClick={(e) => e.stopPropagation()}>
                  <div className={classes('groupActions')}>
                    {allEnabled ? (
                      <UtilButton size="medium" onClick={() => disableAllInGroup(perms)}>
                        {t('disableAll')}
                      </UtilButton>
                    ) : (
                      <UtilButton size="medium" onClick={() => enableAllInGroup(perms)}>
                        {t('enableAll')}
                      </UtilButton>
                    )}
                  </div>
                </div>
              )}
            </div>
          </CollapsibleList.Trigger>

          <CollapsibleList.Content
            className={depth > 0 ? classes('reducedPadding') : undefined}
            innerClassName={depth > 0 ? classes('nestedContent') : undefined}
          >
            <div className={classes('settings')}>
              {permissionGroups.map((group, idx) => {
                const hasMainPermission = group.mainPermission !== null;
                const isBoolean = hasMainPermission && group.mainPermission?.Type === 'boolean';
                const groupKey = hasMainPermission ? group.mainPermission?.Permission : `standalone-${idx}`;

                return (
                  <SettingsGroup key={groupKey}>
                    {/* Render main permission if it exists */}
                    {hasMainPermission &&
                      (() => {
                        const fieldConfig = permissionsConfig.find(
                          (f) => f.fieldName === group.mainPermission!.Permission,
                        );
                        if (!fieldConfig) return null;

                        return renderFormElements({
                          config: [fieldConfig],
                          override: (fieldData) => {
                            if (isBoolean) {
                              return (
                                <SettingsGroup.Row key={group.mainPermission!.Permission}>
                                  <BodyText className={classes('permissionLabelText')} weight="md">
                                    {group.mainPermission!.Label}
                                  </BodyText>
                                  <div className={classes('permissionFieldWrapper')}>
                                    <label
                                      htmlFor={`checkbox-${fieldData.fieldName}`}
                                      style={{ cursor: 'pointer', display: 'block' }}
                                    >
                                      {fieldData.template}
                                    </label>
                                  </div>
                                </SettingsGroup.Row>
                              );
                            }

                            return (
                              <SettingsGroup.Row key={group.mainPermission!.Permission}>
                                <BodyText className={classes('permissionLabelText')} weight="md">
                                  {group.mainPermission!.Label}
                                </BodyText>
                                <div className={classes('permissionFieldWrapper')}>{fieldData.template}</div>
                              </SettingsGroup.Row>
                            );
                          },
                        });
                      })()}

                    {/* Render dependent fields */}
                    {group.dependents.map((permission) => {
                      const fieldConfig = permissionsConfig.find((f) => f.fieldName === permission.Permission);
                      if (!fieldConfig) return null;

                      return renderFormElements({
                        config: [fieldConfig],
                        override: (fieldData) => (
                          <SettingsGroup.Row key={permission.Permission}>
                            <BodyText className={classes('permissionLabelText')} weight="md">
                              {permission.Label}
                            </BodyText>
                            <div className={classes('permissionFieldWrapper')}>{fieldData.template}</div>
                          </SettingsGroup.Row>
                        ),
                      });
                    })}
                  </SettingsGroup>
                );
              })}
            </div>
          </CollapsibleList.Content>
        </CollapsibleList.Item>
      );
    } else {
      // Branch node - render nested sections
      const nestedSections = section.SubItems as SubAccountPermissionSection[];

      // Get total count for all nested permissions
      const stats = countAllPermissionsInSection(section);
      const hasBooleanFields = stats.total > 0;
      const allEnabled = hasBooleanFields && stats.enabled === stats.total;

      // Get all permissions from this section for enable/disable all
      const allPermsInSection = getAllPermissionsFromSection(section);

      return (
        <CollapsibleList.Item key={section.SectionId} open={isFiltering} style={{ '--trigger-depth': depth }}>
          <CollapsibleList.Trigger className={depth === 0 ? styles.baseTrigger : styles.nestedTrigger}>
            <div className={classes('groupHeader')}>
              <div className={classes('groupInfo')}>
                {depth === 0 ? (
                  <Title level="5">{section.SectionName}</Title>
                ) : (
                  <BodyText className={classes('subCollapsibleTitle')}>{section.SectionName}</BodyText>
                )}
                {hasBooleanFields && (
                  <BodyText weight="rg" className={classes('groupCount')}>
                    ({stats.enabled}/{stats.total})
                  </BodyText>
                )}
              </div>
              {hasBooleanFields && !isFiltering && (
                <div onClick={(e) => e.stopPropagation()}>
                  <div className={classes('groupActions')}>
                    {allEnabled ? (
                      <UtilButton size="medium" onClick={() => disableAllInGroup(allPermsInSection)}>
                        {t('disableAll')}
                      </UtilButton>
                    ) : (
                      <UtilButton size="medium" onClick={() => enableAllInGroup(allPermsInSection)}>
                        {t('enableAll')}
                      </UtilButton>
                    )}
                  </div>
                </div>
              )}
            </div>
          </CollapsibleList.Trigger>

          <CollapsibleList.Content
            className={depth > 0 ? classes('reducedPadding') : undefined}
            innerClassName={depth > 0 ? classes('nestedContent') : undefined}
          >
            <CollapsibleList className={depth === 0 ? classes('rootCollapsibleList') : classes('subCollapsibleList')}>
              {nestedSections.map((subSection) => renderSection(subSection, depth + 1))}
            </CollapsibleList>
          </CollapsibleList.Content>
        </CollapsibleList.Item>
      );
    }
  };

  return (
    <div>
      <form onSubmit={ctx.handleSubmit} id="accountPermissionForm" className={classes('formContainer')}>
        {!hasFilteredResults && searchTerm.trim() ? (
          <div className={classes('emptyStateWrapper')}>
            <Box flx="col" ai="center" jc="center" gap="sm">
              <MaterialSymbol icon="manage_search" size="90px" color="rgba(45, 45, 45, 0.2)" />
              <Title level="4">{t('permissionNotFound')}</Title>
            </Box>
          </div>
        ) : (
          <CollapsibleList className={classes('outerList')}>
            {filteredSections.map((section) => renderSection(section, 0))}
          </CollapsibleList>
        )}
      </form>
    </div>
  );
};

export default AccountPermissionForm;
